// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * OÙ MÈNE UNE TÂCHE — module PUR, partagé par les trois applications.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Le défaut (retour de la RQ, 13/09/2026) ────────────────────────────────
 *
 * « Elle clique sur la maintenance qu'elle a en tâche mais rien ne se passe.
 *   Pareil pour sa notification sur le changement du poste de garde. Pareil
 *   pour les tâches à venir : il y a un clic mais rien ne s'affiche. »
 *
 * Les trois symptômes n'en font qu'un : « En retard », « Cette semaine » et
 * « À venir » sont les trois groupes du MÊME panneau.
 *
 * ⚠️ ET LE CLIC NE FAISAIT PAS « RIEN » — c'est plus insidieux que ça. Il
 * naviguait, et déposait sur une LISTE. Le centre des tâches écrivait
 * `/?view=equip&ref=EQ12` ; le Shell lit bien ce `?view=`, fait `go('equip')`,
 * puis range la référence dans `refCible`… que **UN SEUL écran consomme** :
 * celui des maintenances planifiées. Partout ailleurs, la référence était
 * silencieusement perdue et l'on atterrissait sur le registre entier, à charge
 * de retrouver soi-même la pièce. Depuis le tableau de bord, d'où l'on clique
 * la cloche, ça se lit « rien ne s'est passé » ; et sur un écran déjà ouvert,
 * littéralement rien ne bouge.
 *
 * Une indication, donc, pas un lien. Exactement ce que la RQ reprochait déjà
 * le 17/08 à propos des maintenances — corrigé alors pour ce seul cas.
 *
 * ── Ce qui marchait, et qu'il ne fallait surtout pas casser ────────────────
 *
 * ⚠️ TROIS CAS FONCTIONNAIENT, par trois mécanismes différents. Un correctif
 * qui aurait tout basculé sur les chemins les aurait cassés :
 *
 *  · `doc` + référence → `?doc=<code>`, que le Shell résout en fiche ;
 *  · `maintenances` + `planId@date` → `refCible`, que l'écran des maintenances
 *    consomme — et c'est probablement la tâche même que la RQ décrit ;
 *  · les demandes du SITE → `?ref=`, que son inbox a toujours lu.
 *
 * ── Ce qu'on fait, et pourquoi le chemin plutôt que la requête ─────────────
 *
 * Depuis le 14/08/2026 la Qualité adresse chaque fiche par son CHEMIN
 * (`/equipements/EQ12`), avec une résolution référence → fiche déjà éprouvée
 * par les liens partagés : elle attend le registre, distingue « pas encore
 * chargé » de « introuvable », et le DIT. C'est le bon mécanisme ; le centre
 * des tâches ne s'en servait pas.
 *
 * ⚠️ SAUF POUR LES MAINTENANCES PLANIFIÉES, dont la référence n'est pas celle
 * d'une fiche mais d'une OCCURRENCE (`planId@date`) : aucune fiche ne porte ce
 * numéro, et `/maintenances/12@2026-09-10` ne désignerait rien. Elle reste
 * donc en `?ref=`, consommée par l'écran. Deux formes, parce qu'il y a deux
 * natures de référence — pas par accident.
 *
 * ── Pourquoi la table est ICI et non dans l'application Qualité ────────────
 *
 * La cloche existe dans les TROIS applications, et une tâche listée sur le
 * site peut désigner un écran de la Qualité. Le site ne peut donc pas déduire
 * seul `/equipements/EQ12` : il lui faut la table.
 *
 * ⚠️ ELLE EST UN DOUBLE DE `ged/src/lib/adresse.ts`, ET C'EST SURVEILLÉ. Le
 * contrôle `qualite-taches-cliquables` échoue si un slug diverge, si une vue
 * émise par le moteur manque ici, ou si une vue ouvre une fiche d'un côté et
 * pas de l'autre. Il relit TOUS les fichiers qui produisent des tâches, et pas
 * seulement le principal : deux vues (`maintenances`, `metro`) vivent dans des
 * services à part, et c'est en les cherchant qu'on a vu qu'un correctif écrit
 * sur le seul contrôleur les aurait cassées.
 */

/** Le slug d'URL de chaque écran de la Qualité — le même que `ADRESSES`. */
export const SLUGS_GED: Record<string, string> = {
  actions: 'plan-actions',
  /* L'ANNUAIRE (20/10/2026) a remplacé le carnet des correspondants : la tâche « Relevé mensuel à adresser » y
     dépose (le relevé se prépare sur la fiche du correspondant). Sans cette ligne, `cheminTache` ne trouverait
     aucun slug et déposerait sur le tableau de bord : le clic « marcherait » sans rien ouvrir, le défaut exact du
     13/09. Les tâches émises avant la bascule (vue `correspondants`) ouvrent `/correspondants`, que l'écran
     redirige vers l'annuaire. */
  annuaire: 'annuaire',
  correspondants: 'correspondants',
  /* Les STATISTIQUES DES NC (20/10/2026) : un onglet des NC, avec l'envoi par correspondant (CQ et RQ). La tâche
     « Relevé mensuel à adresser » y dépose. */
  statsnc: 'statistiques-nc',
  cqi: 'cqi',
  doc: 'documents',
  equip: 'equipements',
  fichePoste: 'fiche-de-poste',
  maintenances: 'maintenances',
  manuel: 'manuel-prelevement',
  metro: 'metrologie',
  mondossier: 'mon-dossier',
  /* Le parcours d'habilitation (lot 2, 18/10/2026) — la tâche « déclarations
     de compétence à confirmer » mène le tuteur au parcours de SON formé
     (référence = orgId, dans le chemin) ; les événements Competence* mènent
     le formé à son propre parcours. */
  monparcours: 'mon-parcours',
  parcours: 'parcours',
  monplanning: 'mon-planning',
  mespresences: 'mes-presences',
  nc: 'non-conformites',
  postes: 'postes',
  rdv: 'demandes-rendez-vous',
  req: 'demandes',
  /* Retours des usagers (06/10/2026) — la tâche « Retour d'usager à qualifier »
     est née avec le centre des notifications. Sans cette ligne, `cheminTache`
     n'aurait trouvé aucun slug et déposé sur le tableau de bord : le clic aurait
     « marché » sans rien ouvrir, le défaut exact du 13/09. Le contrôle statique
     `qualite-taches-cliquables` interdit d'ailleurs qu'une vue émise par le
     moteur manque ici. */
  retours: 'retours-usagers',
  revueref: 'intervalles-reference',
  /* Revue de contrat & évaluations des tiers (L2, 10/10/2026) — la tâche
     « Revue de contrat à faire » y dépose. */
  revuetiers: 'revue-contrats',
  /* MoMent — référentiel du dépistage T21 (lot L1, 11/10/2026). */
  moment: 'moment',
  momentdossiers: 'moment-dossiers',
  produits: 'produits',
  stock: 'stock',
  utilisateurs: 'utilisateurs',
};

/**
 * Les écrans qui ouvrent UNE fiche par sa référence, dans le CHEMIN.
 *
 * ⚠️ Les autres IGNORENT la référence, ils ne la refusent pas. Poser
 * `/stock/M-2026-004` sur un écran qui ne lit pas de référence donnerait un
 * chemin inconnu, donc un retour au tableau de bord — soit exactement le
 * défaut qu'on corrige. On dépose sur la liste : moins bien qu'une fiche,
 * infiniment mieux que rien.
 */
export const FICHES_GED = new Set(['actions', 'cqi', 'doc', 'equip', 'fichePoste', 'nc', 'parcours']);

/**
 * Les écrans dont la référence désigne autre chose qu'une fiche, et passe donc
 * par la requête — l'écran la consomme via `refCible`.
 *
 * ⚠️ N'EN AJOUTER UNE QU'EN CÂBLANT L'ÉCRAN EN MÊME TEMPS. Une entrée posée
 * ici sans que l'écran lise `refCible` redonne un lien qui dépose sur une
 * liste, c'est-à-dire le défaut d'origine. Les deux écrans câblés à ce jour :
 * `Maintenances.tsx` (`planId@date`) et `DemandesRdv.tsx` (05/10/2026, la
 * référence de la demande).
 */
export const REF_PAR_REQUETE = new Set(['maintenances', 'rdv']);

/**
 * Le chemin d'une tâche, avec le préfixe du module qui la porte.
 *
 * `base` est vide quand la tâche vise l'application courante — on reste alors
 * en navigation interne au lieu de repasser par l'URL publique.
 */
export function cheminTache(
  t: { module: string; vue: string; ref?: string | null },
  base: string,
): string {
  const ref = String(t.ref || '').trim();

  /* ⚠️ `/demandes` DU SITE N'EXISTE PLUS (05/10/2026). La gestion des demandes
     a été retirée du site : tout se traite en Qualité, et le site ne garde
     RIEN (décision d'Inès, même arbitrage que les demandes de compte le
     13/09). Les tâches de rendez-vous sont donc émises par le moteur avec
     `module: 'ged', vue: 'rdv'` et passent par la branche Qualité ci-dessous,
     référence comprise (cf. `REF_PAR_REQUETE`).
     Ce qui reste adressé au site atterrit sur son accueil : il n'a plus de
     back-office à désigner. Un chemin vers la route supprimée aurait donné une
     page introuvable — et un lien mort n'avertit personne, il ouvre autre
     chose. */
  if (t.module === 'site') return `${base}/`;

  const slug = SLUGS_GED[t.vue];
  /* ⚠️ UNE VUE INCONNUE NE DEVIENT PAS LA RACINE. Retomber sur `/` est
     précisément ce qui rend ce genre de défaut invisible : le clic marche, on
     arrive au tableau de bord, et rien ne dit que la destination est perdue.
     On dépose sur le tableau de bord faute de mieux — MAIS le contrôle
     statique interdit qu'une vue émise par le moteur manque ici, pour que ce
     repli ne serve jamais. */
  if (!slug) return `${base}/`;

  if (!ref) return `${base}/${slug}`;

  /* L'occurrence de maintenance (`planId@date`) n'est la référence d'aucune
     fiche : elle reste dans la requête, où l'écran la consomme. */
  if (REF_PAR_REQUETE.has(t.vue)) {
    return `${base}/${slug}?ref=${encodeURIComponent(ref)}`;
  }

  if (FICHES_GED.has(t.vue)) {
    // `encodeURIComponent` : une référence porte parfois une espace
    // (« TEL 1 » au parc d'équipements, « R-STOCK A »).
    return `${base}/${slug}/${encodeURIComponent(ref)}`;
  }
  return `${base}/${slug}`;
}
